import React, { FC } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Wrapper } from './ScheduleReminder.styles';
import { getReminderContentRequest, IReminderContent } from '../../../services/surveyContent';
import { IScheduleReminder, IScheduleSurvey } from '../../../services/surveys';
import { CustomDatePicker } from '../../../shared/DatePicker/DatePicker';
import ScheduleTimePicker from '../../../shared/ScheduleTimePicker/ScheduleTimePicker';
import Checkbox from '../../Checkbox/Checkbox';
import { ContentModal } from '../ContentModal';
import {
  Box,
  Text,
  DeleteReminder,
  OptionLabel,
  FormatOptions,
  FormatOption,
} from '../ScheduleContent/ScheduleContent.styles';

interface ScheduleReminderProps {
  schedule: IScheduleSurvey;
  externalSurveyId: string;
  reminder: IScheduleReminder;
  index: number;
  onUpdateReminder: (id: number, updated: Partial<IScheduleReminder>) => void;
  onDeleteReminder: (id: number) => void;
  isFormatOption?: boolean;
  isModalOptions?: boolean;
  notificationOptions: { label: string; disabled: boolean }[];
}

const ScheduleReminder: FC<ScheduleReminderProps> = ({
  schedule,
  reminder,
  index,
  externalSurveyId,
  onUpdateReminder,
  onDeleteReminder,
  isFormatOption = true,
  isModalOptions = true,
  notificationOptions,
}) => {
  const { t } = useTranslation();
  const { start_date, end_date } = schedule;
  const { execute_at, include_sms } = reminder;
  const params = useParams();
  const surveyId = params.surveyId;
  const parsedDate = execute_at ? dayjs(execute_at) : null;

  const isPastReminder: boolean = parsedDate ? parsedDate.isBefore(dayjs()) : false;

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      onUpdateReminder(index, { execute_at: null });
      return;
    }

    const updated = parsedDate
      ? dayjs(execute_at)
          .set('date', date.date())
          .set('month', date.month())
          .set('year', date.year())
      : date.set('hour', 8).set('minute', 0);

    onUpdateReminder(index, { execute_at: updated.toISOString() });
  };

  const getContent = async (id?: string) => {
    const content: IReminderContent = await getReminderContentRequest({ id });
    return content.reminder_email.translations;
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (!time) return;

    const updated = parsedDate
      ? dayjs(execute_at).set('hour', time.hour()).set('minute', time.minute())
      : dayjs().set('hour', time.hour()).set('minute', time.minute());

    onUpdateReminder(index, { execute_at: updated.toISOString() });
  };

  const handleSmsToggle = () => {
    onUpdateReminder(index, { include_sms: !include_sms });
  };

  const disabledReminderDate = (current: dayjs.Dayjs) =>
    (start_date && current.isBefore(dayjs(start_date), 'day')) ||
    (start_date && current.isSame(dayjs(start_date), 'day')) ||
    (end_date && current.isAfter(dayjs(end_date), 'day')) ||
    false;

  return (
    <Wrapper isPast={isPastReminder}>
      <Box>
        <Text>
          {t('viewSurvey.schedule.reminder')} {index + 1}
        </Text>
      </Box>

      <Box>
        <CustomDatePicker
          onChange={handleDateChange}
          defaultValue={parsedDate}
          disabledDate={disabledReminderDate}
        />
      </Box>

      <Box>
        <ScheduleTimePicker onChange={handleTimeChange} defaultValue={parsedDate} />
      </Box>

      <Box>
        {isFormatOption && (
          <FormatOptions>
            {notificationOptions.map(({ label, disabled }) => (
              <FormatOption key={label}>
                <Checkbox
                  checked={disabled ? true : include_sms}
                  disabled={disabled}
                  onClick={() => !disabled && handleSmsToggle()}
                />
                <OptionLabel>{label}</OptionLabel>
              </FormatOption>
            ))}
          </FormatOptions>
        )}
      </Box>

      <Box>
        {isModalOptions && (
          <ContentModal
            getContent={getContent}
            surveyId={surveyId}
            externalSurveyId={externalSurveyId}
            isReminder
          />
        )}
      </Box>

      <Box>
        <DeleteReminder
          data-testid="delete-reminder-button"
          onClick={() => onDeleteReminder(index)}
        />
      </Box>
    </Wrapper>
  );
};

export default ScheduleReminder;
